import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLifeAreas } from '../lib/stores/lifeareas';
import Card from '../lib/design-system/components/Card';
import Button from '../lib/design-system/components/Button';

export default function LifeWheelPage() {
  const { areas, loading, error, initialize, rateArea, createCustomArea, deleteCustomArea, initialized } = useLifeAreas();
  const [showAddForm, setShowAddForm] = useState(false);
  const [ratings, setRatings] = useState<Record<string, { rating: number; notes: string }>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    icon: '',
    color: '#3B82F6',
  });

  // Debounce timer for autosaving notes
  const notesDebounceTimers = useRef<Record<string, NodeJS.Timeout>>({});

  useEffect(() => {
    if (!initialized) initialize();
  }, [initialized]);

  useEffect(() => {
    // Initialize ratings from areas
    setRatings(prev => {
      const newRatings = { ...prev };
      let changed = false;

      areas.forEach(area => {
        if (!newRatings[area.id]) {
          // Ensure notes is a valid string
          const notes = area.rating_notes && typeof area.rating_notes === 'string'
            ? area.rating_notes
            : '';

          newRatings[area.id] = {
            rating: area.latest_rating ?? 5,
            notes,
          };
          changed = true;
        }
      });

      return changed ? newRatings : prev;
    });
  }, [areas]);

  // Check if there are unsaved changes
  useEffect(() => {
    const changed = areas.some(area => {
      const current = ratings[area.id];
      if (!current) return false;
      return (
        current.rating !== (area.latest_rating ?? 5) ||
        current.notes !== (area.rating_notes ?? '')
      );
    });
    setHasChanges(changed);
  }, [ratings, areas]);

  const handleRatingChange = (areaId: string, rating: number) => {
    setRatings(prev => ({
      ...prev,
      [areaId]: {
        rating,
        notes: prev[areaId]?.notes ?? '',
      },
    }));
  };

  // Autosave notes with debouncing
  const handleNotesChange = useCallback((areaId: string, notes: string) => {
    setRatings(prev => ({
      ...prev,
      [areaId]: {
        rating: prev[areaId]?.rating ?? 5,
        notes,
      },
    }));

    // Clear existing timer
    if (notesDebounceTimers.current[areaId]) {
      clearTimeout(notesDebounceTimers.current[areaId]);
    }

    // Set new timer for autosave (2 seconds after user stops typing)
    notesDebounceTimers.current[areaId] = setTimeout(async () => {
      const area = areas.find(a => a.id === areaId);
      if (area) {
        try {
          const currentRating = ratings[areaId]?.rating ?? area.latest_rating ?? 5;
          console.log(`Autosaving notes for ${area.name}: rating=${currentRating}, notes="${notes.substring(0, 20)}..."`);
          await rateArea(areaId, currentRating, notes || undefined);
          console.log(`Successfully autosaved for ${area.name}`);

          // Keep the notes in local state after save
          setRatings(prev => ({
            ...prev,
            [areaId]: {
              ...prev[areaId],
              rating: currentRating,
              notes,
            },
          }));
        } catch (err) {
          console.error('Failed to autosave notes:', err);
        }
      }
    }, 2000);
  }, [areas, ratings, rateArea]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(notesDebounceTimers.current).forEach(timer => clearTimeout(timer));
    };
  }, []);

  const handleSaveAll = async () => {
    setIsSaving(true);
    setSaveSuccess(false);

    try {
      // Save all changed ratings
      const savePromises = areas.map(area => {
        const current = ratings[area.id];
        if (!current) return null;

        // Only save if rating changed (notes are autosaved)
        if (current.rating !== (area.latest_rating ?? 5)) {
          return rateArea(area.id, current.rating, current.notes || undefined);
        }
        return null;
      }).filter(Boolean);

      await Promise.all(savePromises);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save ratings:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateArea = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name.trim()) {
      try {
        await createCustomArea(formData.name.trim(), formData.icon || '📌', formData.color);
        setFormData({ name: '', icon: '', color: '#3B82F6' });
        setShowAddForm(false);
      } catch (err) {
        console.error('Failed to create custom area:', err);
      }
    }
  };

  const calculateAverageRating = () => {
    if (areas.length === 0) return 0;
    const sum = areas.reduce((acc, area) => acc + (ratings[area.id]?.rating ?? area.latest_rating ?? 5), 0);
    return (sum / areas.length).toFixed(1);
  };

  if (loading && !initialized) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading Life Wheel...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{ marginBottom: 'clamp(1.5rem, 3vw, 2rem)' }}>
        <h1 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
          fontWeight: 400,
          color: 'var(--color-neutral-900)',
          marginBottom: '0.5rem',
        }}>
          Life Wheel
        </h1>
        <p style={{ color: 'var(--color-neutral-600)', fontSize: 'clamp(1rem, 2vw, 1.125rem)' }}>
          Rate each area of your life to see where to focus your energy
        </p>
      </div>

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#DC2626',
          marginBottom: '1.5rem',
        }}>
          {error}
        </div>
      )}

      {/* Overall Score */}
      <Card style={{ padding: 'clamp(1.5rem, 3vw, 2rem)', paddingBottom: '20px', marginBottom: 'clamp(1.5rem, 3vw, 2rem)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center', flex: 1, minWidth: '200px' }}>
            <div style={{ fontSize: 'clamp(2rem, 6vw, 3rem)', fontWeight: 700, color: 'var(--color-primary-600)' }}>
              {calculateAverageRating()}
            </div>
            <div style={{ fontSize: 'clamp(1rem, 2vw, 1.125rem)', color: 'var(--color-neutral-600)', marginTop: '0.5rem' }}>
              Average Life Satisfaction
            </div>
            <div style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)', color: 'var(--color-neutral-500)', marginTop: '0.25rem' }}>
              Based on {areas.length} life areas
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Button onClick={() => setShowAddForm(!showAddForm)}>
              {showAddForm ? 'Cancel' : '+ Add Custom Area'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Add Custom Area Form */}
      {showAddForm && (
        <Card style={{ marginBottom: '2rem', padding: 'clamp(1rem, 2vw, 1.5rem)' }}>
          <form onSubmit={handleCreateArea}>
            <h3 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 'clamp(1.25rem, 2.5vw, 1.5rem)',
              marginBottom: '1rem',
            }}>
              Add Custom Life Area
            </h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontWeight: 600,
                marginBottom: '0.5rem',
                color: 'var(--color-neutral-700)',
                fontSize: '0.875rem',
              }}>
                Area Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g., Creative Projects"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--color-neutral-300)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                  color: 'var(--color-neutral-700)',
                  fontSize: '0.875rem',
                }}>
                  Icon
                </label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="e.g., 🎨"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--color-neutral-300)',
                    borderRadius: '6px',
                    fontSize: '1rem',
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                  color: 'var(--color-neutral-700)',
                  fontSize: '0.875rem',
                }}>
                  Color
                </label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  style={{
                    width: '100%',
                    height: '42px',
                    padding: '0.25rem',
                    border: '1px solid var(--color-neutral-300)',
                    borderRadius: '6px',
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <Button type="submit" disabled={!formData.name.trim()}>
                Add Area
              </Button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1px solid var(--color-neutral-300)',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </Card>
      )}

      {/* Life Areas Grid - Responsive sizing */}
      {areas.length === 0 ? (
        <Card style={{ padding: '3rem', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⭕</div>
          <h3 style={{
            fontFamily: 'var(--font-serif)',
            fontSize: '1.5rem',
            fontWeight: 500,
            color: 'var(--color-neutral-900)',
            marginBottom: '1rem',
          }}>
            No Life Areas Yet
          </h3>
          <p style={{
            color: 'var(--color-neutral-600)',
            marginBottom: '2rem',
            lineHeight: '1.6',
          }}>
            The backend should automatically create 8 default life areas (Career, Health, Relationships, etc.).
            If you're seeing this, there may be a backend issue. Try adding a custom area to get started.
          </p>
          <Button onClick={() => setShowAddForm(true)}>
            + Add Custom Area
          </Button>
        </Card>
      ) : (
        <>
          <div className="grid-standard grid-equal-height" style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-8)' }}>
            {areas.map(area => (
              <Card key={area.id} className="card-compact-desktop">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)', flexShrink: 0 }}>{area.icon}</span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <h3 style={{
                        fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                        fontWeight: 600,
                        color: 'var(--color-neutral-900)',
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {area.name}
                      </h3>
                      {area.is_default === 0 && (
                        <span style={{
                          fontSize: '0.75rem',
                          color: 'var(--color-neutral-500)',
                          fontWeight: 500,
                        }}>
                          Custom
                        </span>
                      )}
                    </div>
                  </div>
                  {area.is_default === 0 && (
                    <button
                      onClick={() => deleteCustomArea(area.id)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--color-error-600)',
                        cursor: 'pointer',
                        fontSize: '1.25rem',
                        flexShrink: 0,
                      }}
                      title="Delete area"
                    >
                      ×
                    </button>
                  )}
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <label style={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: 'var(--color-neutral-700)',
                    }}>
                      Rating
                    </label>
                    <span style={{
                      fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                      fontWeight: 700,
                      color: area.color,
                    }}>
                      {ratings[area.id]?.rating ?? 5}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="1"
                    value={ratings[area.id]?.rating ?? 5}
                    onChange={(e) => handleRatingChange(area.id, parseInt(e.target.value))}
                    style={{
                      width: '100%',
                      height: '8px',
                      accentColor: area.color,
                      cursor: 'pointer',
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    color: 'var(--color-neutral-500)',
                    marginTop: '0.25rem',
                  }}>
                    <span>Low</span>
                    <span>High</span>
                  </div>
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <label style={{
                    display: 'block',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    marginBottom: '0.5rem',
                    color: 'var(--color-neutral-700)',
                  }}>
                    Notes (auto-saved)
                  </label>
                  <textarea
                    value={ratings[area.id]?.notes ?? ''}
                    onChange={(e) => handleNotesChange(area.id, e.target.value)}
                    placeholder="What's going well? What needs attention?"
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid var(--color-neutral-300)',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      minHeight: '60px',
                    }}
                  />
                </div>

                {area.last_rated_at && (
                  <div style={{
                    marginTop: '0.75rem',
                    fontSize: '0.75rem',
                    color: 'var(--color-neutral-500)',
                    textAlign: 'center',
                  }}>
                    Last updated: {new Date(area.last_rated_at).toLocaleDateString()}
                  </div>
                )}
              </Card>
            ))}
          </div>

          {/* Single Save Button at Bottom */}
          <div style={{
            position: 'sticky',
            bottom: 0,
            padding: '1rem 0',
            backgroundColor: 'var(--bg)',
            borderTop: '1px solid var(--color-neutral-200)',
            marginTop: '2rem',
          }}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                onClick={handleSaveAll}
                disabled={!hasChanges || isSaving}
                style={{
                  minWidth: '200px',
                  padding: '1rem 2rem',
                  fontSize: '1.125rem',
                }}
              >
                {isSaving ? 'Saving...' : 'Save All Changes'}
              </Button>

              {saveSuccess && (
                <span style={{
                  color: 'var(--color-success-600)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}>
                  ✓ All ratings saved successfully
                </span>
              )}

              {!hasChanges && !saveSuccess && (
                <span style={{
                  color: 'var(--color-neutral-500)',
                  fontSize: '0.875rem',
                }}>
                  No unsaved changes • Notes auto-save as you type
                </span>
              )}
            </div>
          </div>
        </>
      )}

      {/* Explanation */}
      <Card style={{ marginTop: '2rem', padding: 'clamp(1rem, 2vw, 1.5rem)', backgroundColor: 'var(--color-primary-50)' }}>
        <h3 style={{ marginBottom: '0.75rem', fontWeight: 600, fontSize: 'clamp(1rem, 2vw, 1.125rem)' }}>How to Use the Life Wheel</h3>
        <ul style={{ marginLeft: '1.5rem', lineHeight: '1.8', color: 'var(--color-neutral-700)', fontSize: 'clamp(0.875rem, 1.5vw, 1rem)' }}>
          <li>Rate each area from 0 (needs urgent attention) to 10 (thriving)</li>
          <li>Add notes to capture what's working and what isn't - they save automatically</li>
          <li>Click "Save All Changes" when you're done adjusting ratings</li>
          <li>Review regularly to track progress and shifts over time</li>
          <li>Areas rated 0-3 may need immediate focus</li>
          <li>A balanced wheel has similar ratings across areas</li>
        </ul>
      </Card>
    </div>
  );
}
